import React, { useEffect, useMemo, useState } from "react";

export type SearchableSelectOption = {
    value: string;
    label: string;
};

type SearchableSelectProps = {
    label: string;
    name: string;
    value: string;
    options: SearchableSelectOption[];
    placeholder?: string;
    minSearchLength?: number;
    loading?: boolean;
    loadingText?: string;
    noOptionsText?: string;
    onChange: (name: string, value: string) => void;
    onBlur?: (name: string) => void;
    onSearch?: (query: string) => void;
};

const SearchableSelect = ({
    label,
    name,
    value,
    options,
    placeholder = "Search...",
    minSearchLength = 0,
    loading = false,
    loadingText = "Searching...",
    noOptionsText = "No results found",
    onChange,
    onBlur,
    onSearch,
}: SearchableSelectProps) => {
    const selectedOption = options.find((option) => option.value === value);
    const [query, setQuery] = useState(selectedOption?.label ?? "");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setQuery(selectedOption?.label ?? "");
    }, [selectedOption?.label]);

    useEffect(() => {
        if (!onSearch || selectedOption?.label === query) return;

        const timeoutId = window.setTimeout(() => {
            onSearch(query);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [onSearch, query, selectedOption?.label]);

    const filteredOptions = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery || selectedOption?.label === query) return options;
        return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
    }, [options, query, selectedOption?.label]);

    return (
        <div className="relative flex flex-col gap-1">
            <label className="text-sm font-semibold" htmlFor={name}>{label}</label>
            <input
                id={name}
                name={name}
                type="text"
                value={query}
                placeholder={placeholder}
                autoComplete="off"
                onFocus={() => setOpen(true)}
                onChange={(event) => {
                    const nextQuery = event.target.value;
                    setQuery(nextQuery);
                    setOpen(true);
                    if (value) onChange(name, "");
                }}
                onBlur={() => {
                    window.setTimeout(() => setOpen(false), 120);
                    onBlur?.(name);
                    if (selectedOption && query !== selectedOption.label) {
                        setQuery(selectedOption.label);
                    }
                }}
                className="rounded border px-3 py-2"
            />

            {open && (
                <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
                    {query.trim().length < minSearchLength ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            Type at least {minSearchLength} letters to search
                        </div>
                    ) : loading ? (
                        <div className="px-3 py-2 text-sm text-gray-500">{loadingText}</div>
                    ) : filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    onChange(name, option.value);
                                    setQuery(option.label);
                                    setOpen(false);
                                }}
                            >
                                {option.label}
                            </button>
                        ))
                    ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">{noOptionsText}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
